/*
 * The multiple-choice quiz format.
 *
 *     1. Which organelle releases energy from glucose?
 *     a) Nucleus
 *     *b) Mitochondrion
 *     c) Ribosome
 *
 * A question is a line of text (a number in front is optional and is dropped — the
 * quiz renumbers). Its options follow, one per line, each starting with a letter and
 * `)` or `.`, or with a dash. An asterisk at the start of a line, or at the start of an
 * option's text, marks the one correct answer. A blank line, or any line that is not an
 * option once options have begun, starts the next question. Options are lettered with
 * letters, never numbers, so a numbered question can follow directly without a blank
 * line and still be read as a question.
 */

import { shuffle } from "@/lib/tool-kit/classroom/random";

export const MAX_QUIZ_OPTIONS = 8;

const LINE_BREAK = /\r?\n/;
const OPTION_LINE = /^(\*)?\s*(?:\(?[A-Za-z][.)]|[-•])(?:\s+(.*))?$/;
const QUESTION_NUMBER = /^(?:q(?:uestion)?\s*)?\d+\s*[.):]\s*/i;
const MARKED_TEXT = /^\*\s*/;
/** Options that refer to other options keep their position when options are shuffled. */
const REFERS_TO_OTHERS = /\babove\b|\b[a-h]\s*(?:and|&|or|nor)\s*[a-h]\b/i;

export interface QuizQuestion {
  stem: string;
  options: string[];
  /** Index into `options` of the correct answer. */
  correct: number;
  /** Options that must not move when options are shuffled. */
  pinned: boolean[];
  /** 1-based line of the question text. */
  line: number;
}

export type QuizErrorKind =
  | "option-before-question"
  | "no-options"
  | "one-option"
  | "too-many-options"
  | "empty-option"
  | "no-correct"
  | "several-correct";

export interface QuizError {
  kind: QuizErrorKind;
  /** 1-based position of the question among all questions written, 0 if none. */
  question: number;
  /** 1-based line the problem was found on. */
  line: number;
}

interface Draft {
  stem: string[];
  options: { text: string; correct: boolean; line: number }[];
  line: number;
  ordinal: number;
}

function readOption(line: string): { text: string; correct: boolean } | null {
  const match = OPTION_LINE.exec(line);
  if (!match) {
    return null;
  }
  const raw = (match[2] ?? "").trim();
  const markedInText = MARKED_TEXT.test(raw);
  return {
    text: raw.replace(MARKED_TEXT, "").trim(),
    correct: match[1] === "*" || markedInText,
  };
}

function checkDraft(draft: Draft): QuizError | null {
  const error = (kind: QuizErrorKind, line = draft.line): QuizError => ({
    kind,
    question: draft.ordinal,
    line,
  });
  const { options } = draft;
  if (options.length === 0) {
    return error("no-options");
  }
  if (options.length === 1) {
    return error("one-option");
  }
  if (options.length > MAX_QUIZ_OPTIONS) {
    return error("too-many-options");
  }
  const empty = options.find((option) => option.text.length === 0);
  if (empty) {
    return error("empty-option", empty.line);
  }
  const marked = options.filter((option) => option.correct).length;
  if (marked === 0) {
    return error("no-correct");
  }
  if (marked > 1) {
    return error("several-correct");
  }
  return null;
}

function toQuestion(draft: Draft): QuizQuestion {
  return {
    stem: draft.stem.join(" "),
    options: draft.options.map((option) => option.text),
    correct: draft.options.findIndex((option) => option.correct),
    pinned: draft.options.map((option) => REFERS_TO_OTHERS.test(option.text)),
    line: draft.line,
  };
}

/** Close a question: keep it if it is well formed, record why if it is not. */
function settle(
  draft: Draft | null,
  questions: QuizQuestion[],
  errors: QuizError[]
): null {
  if (draft) {
    const problem = checkDraft(draft);
    if (problem) {
      errors.push(problem);
    } else {
      questions.push(toQuestion(draft));
    }
  }
  return null;
}

export function parseQuiz(text: string): {
  questions: QuizQuestion[];
  errors: QuizError[];
} {
  const questions: QuizQuestion[] = [];
  const errors: QuizError[] = [];
  let draft: Draft | null = null;
  let ordinal = 0;

  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    const line = raw.trim();
    const lineNumber = index + 1;
    if (line.length === 0) {
      draft = settle(draft, questions, errors);
      continue;
    }
    const option = readOption(line);
    if (option) {
      if (draft) {
        draft.options.push({ ...option, line: lineNumber });
      } else {
        errors.push({
          kind: "option-before-question",
          question: 0,
          line: lineNumber,
        });
      }
      continue;
    }
    if (draft && draft.options.length === 0) {
      draft.stem.push(line);
      continue;
    }
    draft = settle(draft, questions, errors);
    ordinal += 1;
    draft = {
      stem: [line.replace(QUESTION_NUMBER, "")],
      options: [],
      line: lineNumber,
      ordinal,
    };
  }
  settle(draft, questions, errors);
  return { questions, errors };
}

/**
 * A display order for one question's options: position → original option index.
 * Pinned options ("All of the above", "A and B") stay where the author put them.
 */
export function shuffledOptionOrder(
  question: QuizQuestion,
  random: () => number
): number[] {
  const free = question.options
    .map((_, index) => index)
    .filter((index) => !question.pinned[index]);
  const moved = shuffle(free, random);
  let next = 0;
  return question.options.map((_, index) => {
    if (question.pinned[index]) {
      return index;
    }
    const source = moved[next] as number;
    next += 1;
    return source;
  });
}

/** The identity order, for an unshuffled quiz. */
export function naturalOrder(question: QuizQuestion): number[] {
  return question.options.map((_, index) => index);
}

/** How many answers match, given each question's display order and chosen position. */
export function countCorrect(
  questions: readonly QuizQuestion[],
  orders: readonly (readonly number[])[],
  chosen: readonly (number | null)[]
): number {
  let correct = 0;
  for (const [index, question] of questions.entries()) {
    const position = chosen[index];
    if (position === null || position === undefined) {
      continue;
    }
    if (orders[index]?.[position] === question.correct) {
      correct += 1;
    }
  }
  return correct;
}

/** A, B, C … for a display position. */
export function optionLetter(position: number): string {
  return String.fromCharCode(65 + position);
}

/** One question as it is shown: its parsed index and its option order. */
export interface ShownQuestion {
  question: QuizQuestion;
  /** Position in the parsed list — the key for a student's answer. */
  index: number;
  /** Display position → original option index. */
  order: number[];
}

/** A shuffle, remembered with the text it was made for so an edit invalidates it. */
export interface QuizShuffle {
  text: string;
  questions: number[];
  options: number[][];
}

export function shuffleQuiz(
  text: string,
  questions: readonly QuizQuestion[],
  random: () => number
): QuizShuffle {
  return {
    text,
    questions: shuffle(
      questions.map((_, index) => index),
      random
    ),
    options: questions.map((question) => shuffledOptionOrder(question, random)),
  };
}

/** The questions in display order, each with its option order. */
export function arrangeQuiz(
  questions: readonly QuizQuestion[],
  shuffled: QuizShuffle | null,
  mixQuestions: boolean,
  mixOptions: boolean
): ShownQuestion[] {
  const order =
    shuffled && mixQuestions
      ? shuffled.questions
      : questions.map((_, index) => index);
  return order.map((index) => {
    const question = questions[index] as QuizQuestion;
    const mixed = shuffled && mixOptions ? shuffled.options[index] : undefined;
    return { question, index, order: mixed ?? naturalOrder(question) };
  });
}

/** The letter of the correct option as displayed. */
export function answerLetter(item: ShownQuestion): string {
  return optionLetter(item.order.indexOf(item.question.correct));
}

/** The quiz as plain text, in display order, for pasting into a document. */
export function quizAsText(
  title: string,
  instructions: string,
  shown: readonly ShownQuestion[]
): string {
  return [
    title,
    instructions,
    "",
    ...shown.flatMap((item, number) => [
      `${number + 1}. ${item.question.stem}`,
      ...item.order.map(
        (source, position) =>
          `   ${optionLetter(position)}. ${item.question.options[source]}`
      ),
      "",
    ]),
  ]
    .join("\n")
    .trim();
}
